import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Row, Col, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone, faLocationDot, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { FaBed, FaBath, FaCar, FaTape, FaHome } from 'react-icons/fa';

import { validateEmail } from '../utils/validate';
import { handleTokenAndPostRequest, formatCurrency, trackEvent } from '../utils/utils';
import { computeBranding } from '../utils/useAgentDetails';

function RequestAppraisalModal({ show, onHide, agentDetails, propertyAddress, propertyDetails }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasSavedSettings = useRef(false);

  // Build a consolidated notes string from all sources so the backend always has notes
  const buildAllNotes = () => {
    const parts = [];
    if (propertyDetails?.propertyNotes) {
      parts.push(typeof propertyDetails.propertyNotes === 'string' ? propertyDetails.propertyNotes : JSON.stringify(propertyDetails.propertyNotes));
    }
    if (propertyDetails?.userNotes && propertyDetails.userNotes.length > 0) {
      const userNoteTexts = propertyDetails.userNotes
        .map(n => typeof n === 'object' ? (n.note || n.text || '') : String(n))
        .filter(Boolean);
      if (userNoteTexts.length > 0) parts.push('User Notes: ' + userNoteTexts.join('; '));
    }
    if (propertyDetails?.agentNotes && propertyDetails.agentNotes.length > 0) {
      const agentNoteTexts = propertyDetails.agentNotes
        .map(n => typeof n === 'object' ? (n.note || n.text || '') : String(n))
        .filter(Boolean);
      if (agentNoteTexts.length > 0) parts.push('Agent Notes: ' + agentNoteTexts.join('; '));
    }
    return parts.join('\n') || null;
  };

  useEffect(() => {
    if (show && agentDetails) {
      // Pre-populate email from user details
      if (agentDetails.user_email) {
        setEmail(agentDetails.user_email);
      }

      // Call save-settings when modal is opened
      if (!hasSavedSettings.current) {
        hasSavedSettings.current = true;
        const saveSettings = async () => {
          try {
            await handleTokenAndPostRequest(
              `${window.api_host}/save-settings`,
              {
                agent_slug: agentDetails.landing_page_slug,
                event_type: 'start_request_appraisal_review',
                metadata: {
                  property_address: propertyAddress || '',
                  bedrooms: propertyDetails?.bedrooms ?? null,
                  bathrooms: propertyDetails?.bathrooms ?? null,
                  carspaces: propertyDetails?.carspaces ?? null,
                  land_area: propertyDetails?.landArea ?? null,
                  property_type: propertyDetails?.propertyType
                    ? (typeof propertyDetails.propertyType === 'object' ? propertyDetails.propertyType.value : propertyDetails.propertyType)
                    : null,
                  property_notes: buildAllNotes(),
                  user_notes: propertyDetails?.userNotes ?? [],
                  agent_notes: propertyDetails?.agentNotes ?? [],
                  valuation: propertyDetails?.valuation ?? null,
                  valuation_lower: propertyDetails?.valuationLower ?? null,
                  valuation_upper: propertyDetails?.valuationUpper ?? null,
                  rental_estimate: propertyDetails?.rentalEstimate ?? null,
                  rental_estimate_lower: propertyDetails?.rentalEstimateLower ?? null,
                  rental_estimate_upper: propertyDetails?.rentalEstimateUpper ?? null,
                  days_to_sell: propertyDetails?.daysToSell ?? null,
                  days_to_sell_lower: propertyDetails?.daysToSellLower ?? null,
                  days_to_sell_upper: propertyDetails?.daysToSellUpper ?? null,
                  ai_summary: propertyDetails?.aiSummary ?? null,
                },
              },
              {
                'Content-Type': 'application/json',
              }
            );
          } catch (error) {
            console.error('Error saving settings', error);
          }
        };
        saveSettings();
      }
    }
  }, [show, agentDetails]);

  const resetForm = () => {
    setEmail(agentDetails?.user_email || '');
    setPhone('');
    setMessage('');
    setIsSubmitting(false);
    hasSavedSettings.current = false;
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email) {
      setMessage('Please provide your email address so the agent can get back to you.');
      return;
    }
    if (validateEmail(email)) {
      setMessage('Please enter a valid email address.');
      return;
    }
    trackEvent('submit_request_appraisal');
    setIsSubmitting(true);

    try {
      // Call save-settings with metadata on submit
      await handleTokenAndPostRequest(
        `${window.api_host}/save-settings`,
        {
          agent_slug: agentDetails.landing_page_slug,
          event_type: 'submit_request_appraisal_review',
          metadata: {
            property_address: propertyAddress || '',
            bedrooms: propertyDetails?.bedrooms ?? null,
            bathrooms: propertyDetails?.bathrooms ?? null,
            carspaces: propertyDetails?.carspaces ?? null,
            land_area: propertyDetails?.landArea ?? null,
            property_type: propertyDetails?.propertyType
              ? (typeof propertyDetails.propertyType === 'object' ? propertyDetails.propertyType.value : propertyDetails.propertyType)
              : null,
            property_notes: buildAllNotes(),
            user_notes: propertyDetails?.userNotes ?? [],
            agent_notes: propertyDetails?.agentNotes ?? [],
            valuation: propertyDetails?.valuation ?? null,
            valuation_lower: propertyDetails?.valuationLower ?? null,
            valuation_upper: propertyDetails?.valuationUpper ?? null,
            rental_estimate: propertyDetails?.rentalEstimate ?? null,
            rental_estimate_lower: propertyDetails?.rentalEstimateLower ?? null,
            rental_estimate_upper: propertyDetails?.rentalEstimateUpper ?? null,
            days_to_sell: propertyDetails?.daysToSell ?? null,
            days_to_sell_lower: propertyDetails?.daysToSellLower ?? null,
            days_to_sell_upper: propertyDetails?.daysToSellUpper ?? null,
            ai_summary: propertyDetails?.aiSummary ?? null,
            email,
            phone,
          },
        },
        {
          'Content-Type': 'application/json',
        }
      );

      setIsSubmitting(false);
      resetForm();
      onHide();
    } catch (error) {
      console.error('Error:', error);
      setIsSubmitting(false);
      setMessage('There was an error submitting your request. Please try again.');
    }
  };

  if (!agentDetails) return null;

  const { accentColor, buttonTextColor, isLight } = computeBranding(agentDetails) || {};
  const agentFirstName = agentDetails.agent_name?.split(' ')[0] || 'the agent';

  const inputStyle = {
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    padding: '11px 14px',
    fontSize: '0.88rem',
  };

  return (
    <Modal show={show} onHide={handleClose} fullscreen="xl-down" style={{ zIndex: 9999 }}>
      <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }}>
        <Modal.Title style={{ fontSize: '1.2rem', fontWeight: 700, color: '#111', letterSpacing: '-0.01em' }}>Request Appraisal Review</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ overflowY: 'auto', padding: '16px 24px' }}>
        <p style={{ marginBottom: '16px', fontSize: '0.88rem', color: '#6b7280', lineHeight: 1.6 }}>
          {agentFirstName} will review your AI-generated estimate and comparables analysis based on the property details you've set, and get back to you with any adjustments if necessary.
        </p>

        {propertyDetails && (
          <div style={{ backgroundColor: '#f9fafb', borderRadius: 12, padding: '14px 16px', marginBottom: '16px', fontSize: '0.85rem', border: '1px solid #f3f4f6' }}>
            <div style={{ fontWeight: 600, marginBottom: '8px', color: '#111', fontSize: '0.82rem' }}>Your Current Property Details</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '6px', color: '#374151' }}>
              {propertyDetails.bedrooms != null && (
                <span><FaBed style={{ marginRight: 4, marginBottom: 2, color: '#9ca3af' }} />{propertyDetails.bedrooms} Beds</span>
              )}
              {propertyDetails.bathrooms != null && (
                <span><FaBath style={{ marginRight: 4, marginBottom: 2, color: '#9ca3af' }} />{propertyDetails.bathrooms} Baths</span>
              )}
              {propertyDetails.carspaces != null && (
                <span><FaCar style={{ marginRight: 4, marginBottom: 2, color: '#9ca3af' }} />{propertyDetails.carspaces} Cars</span>
              )}
              {propertyDetails.landArea != null && (
                <span><FaTape style={{ marginRight: 4, marginBottom: 2, color: '#9ca3af' }} />{propertyDetails.landArea} sqm</span>
              )}
              {propertyDetails.propertyType && (
                <span><FaHome style={{ marginRight: 4, marginBottom: 2, color: '#9ca3af' }} />{typeof propertyDetails.propertyType === 'object' ? propertyDetails.propertyType.label : propertyDetails.propertyType}</span>
              )}
            </div>
            {propertyDetails.propertyNotes && (
              <div style={{ marginBottom: '4px', color: '#374151' }}>
                <span style={{ fontWeight: 500 }}>Notes: </span>
                {(() => {
                  const notes = propertyDetails.propertyNotes;
                  if (typeof notes !== 'string') {
                    if (Array.isArray(notes)) return notes.map(n => typeof n === 'object' ? (n.note || n.text || n.label || JSON.stringify(n)) : String(n)).join(', ');
                    if (typeof notes === 'object' && notes !== null) return notes.note || notes.text || notes.label || JSON.stringify(notes);
                    return String(notes);
                  }
                  try {
                    const parsed = JSON.parse(notes);
                    if (Array.isArray(parsed)) return parsed.map(n => typeof n === 'object' ? (n.note || n.text || n.label || JSON.stringify(n)) : String(n)).join(', ');
                    if (typeof parsed === 'object' && parsed !== null) return parsed.note || parsed.text || parsed.label || JSON.stringify(parsed);
                    return String(parsed);
                  } catch {
                    return notes;
                  }
                })()}
              </div>
            )}
            {propertyDetails.userNotes && propertyDetails.userNotes.length > 0 && (
              <div style={{ marginBottom: '4px', color: '#374151' }}>
                <span style={{ fontWeight: 500 }}>Your Notes: </span>
                {propertyDetails.userNotes.map(n => typeof n === 'object' ? (n.note || n.text || '') : String(n)).filter(Boolean).join('; ')}
              </div>
            )}
            {propertyDetails.agentNotes && propertyDetails.agentNotes.length > 0 && (
              <div style={{ marginBottom: '4px', color: '#374151' }}>
                <span style={{ fontWeight: 500 }}>Agent Notes: </span>
                {propertyDetails.agentNotes.map(n => typeof n === 'object' ? (n.note || n.text || '') : String(n)).filter(Boolean).join('; ')}
              </div>
            )}
            {propertyDetails.valuation && (
              <div style={{ marginTop: '8px', fontWeight: 600, color: '#111' }}>
                AI Estimate: {formatCurrency(propertyDetails.valuationLower)} – {formatCurrency(propertyDetails.valuation)} – {formatCurrency(propertyDetails.valuationUpper)}
              </div>
            )}
          </div>
        )}

        <Form className="w-100" onSubmit={handleSubmit}>
          <Form.Label style={{ fontSize: '0.78rem', marginBottom: '4px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Property Address</Form.Label>
          <Form.Control
            value={propertyAddress || ''}
            disabled
            style={{ ...inputStyle, backgroundColor: '#f9fafb', marginBottom: 10 }}
          />

          <Form.Label style={{ fontSize: '0.78rem', marginBottom: '4px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Email</Form.Label>
          <Form.Control
            type="email"
            placeholder="Your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ ...inputStyle, marginBottom: 10 }}
          />

          <Form.Label style={{ fontSize: '0.78rem', marginBottom: '4px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Phone</Form.Label>
          <Form.Control
            type="tel"
            placeholder="Your phone number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            style={{ ...inputStyle, marginBottom: 10 }}
          />

          {message && (
            <Alert variant="secondary" className="mt-2" style={{ borderRadius: 12, border: 'none', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '0.88rem', textAlign: 'center' }}>
              {message}
            </Alert>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer style={{ border: 'none', paddingTop: 8, paddingBottom: 20, paddingLeft: 24, paddingRight: 24 }}>
        <Button
          onClick={handleSubmit}
          className="w-100"
          disabled={isSubmitting}
          style={{
            backgroundColor: accentColor,
            borderColor: isLight ? '#222' : accentColor,
            color: buttonTextColor,
            borderRadius: 12,
            padding: '13px 0',
            fontWeight: 600,
            fontSize: '0.95rem',
            boxShadow: `0 4px 16px ${accentColor}30`,
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          {isSubmitting ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 8 }} />
              Request Review
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RequestAppraisalModal;
